/**
 * The two decisions the error boundary makes, as functions with no DOM in them.
 *
 * Split out for the same reason `combobox.logic.ts` is: this console has no
 * jsdom and no React testing library, so anything that can only be exercised by
 * mounting a component is in practice untested. These two are precisely the
 * parts worth testing — one classifies an error, the other decides whether to
 * reload the page, and getting the second wrong means a tab that reloads
 * forever.
 */

/** Where the last forced reload is recorded, so we can decline to do it twice. */
export const RELOAD_MARK = 'fv:chunk-reload';

/**
 * Long enough that the reload has certainly happened and failed again, short
 * enough that a chunk failure tomorrow is still recovered automatically.
 */
export const RELOAD_COOLDOWN_MS = 30_000;

/**
 * Vite, webpack and the three engines each phrase this differently, and the
 * MIME-TYPE WORDING IS THE ONE THAT MATTERS MOST: when a hashed chunk is missing,
 * the SPA history fallback answers with `index.html` rather than a 404, so what
 * the browser complains about is a content type. That is the exact shape of the
 * stale-document failure this whole mechanism exists to recover from, and an
 * earlier version of this pattern missed it — Chrome says "responded with a MIME
 * type of", not "not a valid MIME type", and only the test caught the
 * difference.
 *
 * Matched on FRAGMENTS rather than whole sentences for that reason. `module
 * script` covers Safari's "Importing a module script failed" and Chrome's
 * "Failed to load module script" together, and nothing in an application error
 * says either by accident. A false positive costs one reload, which is guarded
 * to happen at most once; a false negative costs a blank page.
 */
const CHUNK_FAILURE = /dynamically imported module|module script|Loading chunk|ChunkLoadError|MIME type/i;

/**
 * Whether this error is a `lazy()` import that failed to fetch, rather than a
 * component that threw.
 *
 * The distinction decides what the user is told and whether the page reloads
 * itself. A stale document is fixed by reloading; a bug in a report is not, and
 * reloading into it repeatedly would just hide the message that explains it.
 */
export function isChunkFailure(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.name === 'ChunkLoadError' || CHUNK_FAILURE.test(error.message);
}

/**
 * Whether a forced reload is allowed right now, given the recorded mark.
 *
 * FAILS CLOSED ON A MISSING OR UNREADABLE MARK IS THE WRONG DEFAULT HERE — a
 * first failure has no mark, and that is the case the whole mechanism exists
 * for. What it must refuse is a SECOND reload inside the cooldown, because that
 * is a deploy that is genuinely broken and a tab that would spin on it forever.
 *
 * A mark that does not parse as a number is treated as no mark: something else
 * wrote the key, and one reload is a smaller harm than never recovering.
 */
export function shouldReload(mark: string | null, now: number): boolean {
  if (mark === null || mark === '') return true;
  const last = Number(mark);
  if (!Number.isFinite(last)) return true;
  // A mark from the future — a clock that moved backwards — is not evidence
  // that we reloaded recently, but it is evidence we cannot measure the gap.
  // Declining once is recoverable; looping is not.
  if (last > now) return false;
  return now - last >= RELOAD_COOLDOWN_MS;
}
